import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Stack,
  TextField,
  Button,
  Typography,
  Alert,
} from '@mui/material';
import { useAuth } from '../../hooks/useAuth';
import { palette, typography } from 'theme/tokens';

/**
 * First-login password rotation for the CRM portal.
 *
 * A portal user is created with a GENERATED password and
 * `partner_users.must_change_password = 1`; sign-in returns that flag and routes
 * here. The route did not exist, so it rendered blank — a working sign-in that
 * dead-ended for every newly created account.
 *
 * The rotation is server-side (POST /app/v1/partner/auth/change-password, which
 * serves both portals). The acting user comes from the verified token, never the
 * body, so this form cannot change anyone else's password. No new token is
 * issued: the one already held stays valid and only the cached flag clears.
 *
 * The screen no longer asks for the temporary password — the user typed it on
 * the sign-in screen one step ago and the auth context still holds it in memory
 * for exactly this. If that memory was lost (e.g. a reload landed back here),
 * `changePassword` reports `needsReauth` and this screen sends them back to sign
 * in rather than asking for the password a second time.
 */
export default function ChangePasswordPage() {
  const { changePassword, logout, user } = useAuth();
  const navigate = useNavigate();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!newPassword) {
      setError('Choose a new password.');
      return;
    }
    // 8 mirrors the server's changePasswordSchema rather than inventing a second
    // policy — a shorter value would be refused there anyway.
    if (newPassword.length < 8) {
      setError('Your new password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Both passwords must match.');
      return;
    }

    setSaving(true);
    try {
      const result = await changePassword(newPassword);
      if (!result.ok) {
        if (result.needsReauth) {
          await logout();
          navigate('/admin/login', { replace: true });
          return;
        }
        setError(result.message);
        return;
      }
      navigate('/admin', { replace: true });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: palette.neutral[40],
        p: 3,
      }}
    >
      <Paper elevation={0} sx={{ width: '100%', maxWidth: 440, p: 5, borderRadius: 3 }}>
        <Typography
          variant="overline"
          sx={{ color: 'primary.main', fontWeight: typography.fontWeight.bold, letterSpacing: '0.08em' }}
        >
          One more step
        </Typography>

        <Typography variant="h5" sx={{ fontWeight: typography.fontWeight.bold, mt: 1, mb: 1 }}>
          Choose a password
        </Typography>

        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {user?.email
            ? `${user.email} is signed in with a temporary password. Pick your own to continue.`
            : 'Your account is signed in with a temporary password. Pick your own to continue.'}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mt: 2.5 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
          <Stack spacing={2.5}>
            <TextField
              label="New password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              helperText="At least 8 characters"
              fullWidth
            />
            <TextField
              label="Confirm new password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              fullWidth
            />
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={saving}
              sx={{ height: 48, borderRadius: 2, fontWeight: typography.fontWeight.semiBold }}
            >
              {saving ? 'Saving…' : 'Save and continue'}
            </Button>
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
}
